import React, { useEffect, useRef, useState } from 'react'
import { Card, Form, Button, Spinner, Alert, ProgressBar } from 'react-bootstrap'
import { testCameraConnection, getDetectedSubnets, startNetworkScan, getScanStatus } from '../../generic/crud'
import { showToast, TOAST_SUCCESS } from '../../../utils/toast/Toast'

const getDefaultPort = (protocol) => ({ rtsp: '554', http: '80', https: '443' }[protocol] || '')

// CAMERA half of a setup group: protocol/host/port/user/pass/path fields plus
// Test Connection and an inline Scan Network panel. `camera` + `onChange(field,value)`
// are owned by the parent SetupGroupCard.
const CameraConfigSection = ({ camera: cameraProp, onChange }) => {
    // Guard against a null camera (a fresh setup group can arrive without one) so the
    // render never crashes reading camera.protocol.
    const camera = cameraProp || {}
    const [testing, setTesting] = useState(false)
    const [testImage, setTestImage] = useState(null)
    const [testError, setTestError] = useState(null)

    const [scanView, setScanView] = useState(false)
    const [scanning, setScanning] = useState(false)
    const [scanConfig, setScanConfig] = useState({ protocol: 'rtsp', username: '', password: '', subnet: '', brand: 'auto' })
    const [scanResults, setScanResults] = useState([])
    const [scanMessage, setScanMessage] = useState(null)
    const [scanSubnets, setScanSubnets] = useState([])
    const [scanProgress, setScanProgress] = useState({ percent: 0, phase: '' })
    const scanPollRef = useRef(null)

    // Stop any in-flight scan poll when the section unmounts.
    useEffect(() => () => { if (scanPollRef.current) clearInterval(scanPollRef.current) }, [])

    const handleTest = async () => {
        setTesting(true); setTestImage(null); setTestError(null)
        try {
            const response = await testCameraConnection(camera)
            if (response.data.success) {
                setTestImage({ data: response.data.imageData, contentType: response.data.contentType || 'image/jpeg' })
            } else {
                setTestError(response.data.message || 'Connection failed')
            }
        } catch (err) {
            setTestError(err.response?.data?.message || 'Failed to connect to camera')
        } finally {
            setTesting(false)
        }
    }

    const stopPoll = () => { if (scanPollRef.current) { clearInterval(scanPollRef.current); scanPollRef.current = null } }

    const openScanView = async () => {
        stopPoll()
        setScanConfig({ protocol: 'rtsp', username: '', password: '', subnet: '', brand: 'auto' })
        setScanResults([]); setScanMessage(null); setScanSubnets([]); setScanProgress({ percent: 0, phase: '' })
        setScanning(false); setScanView(true)
        try {
            const resp = await getDetectedSubnets()
            const subnets = resp.data.subnets || []
            if (subnets.length > 0) setScanConfig(prev => ({ ...prev, subnet: subnets[0] }))
        } catch (e) { /* user can type manually */ }
    }

    const handleScan = async () => {
        setScanning(true); setScanResults([]); setScanMessage(null); setScanSubnets([]); setScanProgress({ percent: 0, phase: 'starting' })
        try {
            const response = await startNetworkScan(scanConfig.protocol, scanConfig.username, scanConfig.password, scanConfig.subnet, scanConfig.brand)
            if (!response.data.success) { setScanMessage(response.data.message || 'Scan failed'); setScanning(false); return }
            const scanId = response.data.scanId
            setScanSubnets(response.data.subnets || [])
            scanPollRef.current = setInterval(async () => {
                try {
                    const d = (await getScanStatus(scanId)).data
                    const phase = d.phase || 'scanning'
                    if (phase === 'verifying') setScanProgress({ percent: 100, phase: 'verifying', verifyPercent: d.verifyPercent || 0 })
                    else setScanProgress({ percent: d.percent || 0, phase })
                    if (phase === 'done') { stopPoll(); setScanResults(d.cameras || []); setScanMessage(d.message); setScanning(false) }
                } catch (e) { stopPoll(); setScanMessage('Lost connection to scan'); setScanning(false) }
            }, 1000)
        } catch (err) {
            setScanMessage(err.response?.data?.message || 'Network scan failed'); setScanning(false)
        }
    }

    const handleSelectCamera = (cam) => {
        onChange('protocol', cam.protocol || camera.protocol)
        onChange('host', cam.host)
        onChange('port', cam.port)
        if (scanConfig.username) onChange('username', scanConfig.username)
        if (scanConfig.password) onChange('password', scanConfig.password)
        if (cam.streamPath) onChange('path', cam.streamPath)
        setScanView(false); setTestImage(null); setTestError(null)
        showToast('Camera selected: ' + cam.host + ':' + cam.port, TOAST_SUCCESS)
    }

    const renderScanView = () => (
        <>
            <Form.Group className="mb-3">
                <Form.Label className="small fw-semibold text-muted mb-1">Protocol</Form.Label>
                <Form.Select size="sm" value={scanConfig.protocol} disabled={scanning}
                    onChange={(e) => setScanConfig(p => ({ ...p, protocol: e.target.value }))}>
                    <option value="rtsp">RTSP</option><option value="http">HTTP</option><option value="https">HTTPS</option>
                </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
                <Form.Label className="small fw-semibold text-muted mb-1">Camera Brand</Form.Label>
                <Form.Select size="sm" value={scanConfig.brand} disabled={scanning}
                    onChange={(e) => setScanConfig(p => ({ ...p, brand: e.target.value }))}>
                    <option value="auto">Auto Detect (try all)</option>
                    <option value="Hikvision">Hikvision</option>
                    <option value="Dahua">Dahua / Amcrest</option>
                    <option value="CP Plus">CP Plus</option>
                    <option value="Axis">Axis</option>
                    <option value="Hanwha">Hanwha / Samsung</option>
                    <option value="Uniview">Uniview</option>
                    <option value="Reolink">Reolink</option>
                    <option value="EZVIZ">EZVIZ</option>
                </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
                <Form.Label className="small fw-semibold text-muted mb-1">Subnet <span className="text-danger">*</span></Form.Label>
                <Form.Control size="sm" type="text" value={scanConfig.subnet} disabled={scanning}
                    onChange={(e) => setScanConfig(p => ({ ...p, subnet: e.target.value }))} placeholder="e.g. 10.53.1 or 192.168.1" />
                <Form.Text className="text-muted small">
                    {scanConfig.subnet && scanConfig.subnet.split('.').length === 2
                        ? `Will scan ${scanConfig.subnet}.*.* (/16 — 65,024 IPs)`
                        : scanConfig.subnet && scanConfig.subnet.split('.').length === 3
                            ? `Will scan ${scanConfig.subnet}.* (/24 — 254 IPs)` : 'e.g. 10.53.1 to scan 10.53.1.*'}
                </Form.Text>
            </Form.Group>
            <div className="row mb-3">
                <div className="col-6"><Form.Group>
                    <Form.Label className="small fw-semibold text-muted mb-1">Username</Form.Label>
                    <Form.Control size="sm" type="text" value={scanConfig.username} disabled={scanning}
                        onChange={(e) => setScanConfig(p => ({ ...p, username: e.target.value }))} placeholder="admin" />
                </Form.Group></div>
                <div className="col-6"><Form.Group>
                    <Form.Label className="small fw-semibold text-muted mb-1">Password</Form.Label>
                    <Form.Control size="sm" type="password" value={scanConfig.password} disabled={scanning}
                        onChange={(e) => setScanConfig(p => ({ ...p, password: e.target.value }))} placeholder="********" />
                </Form.Group></div>
            </div>
            <div className="d-flex gap-2 mb-3">
                <Button variant="primary" size="sm" onClick={handleScan} disabled={scanning || !scanConfig.subnet}>
                    {scanning ? <><Spinner size="sm" animation="border" className="me-1" />Scanning...</> : <><i className="bi bi-search me-1"></i>Start Scan</>}
                </Button>
                <Button variant="light" size="sm" onClick={() => setScanView(false)}><i className="bi bi-arrow-left me-1"></i>Back</Button>
            </div>
            {scanning && (
                <div className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                        <span className="small text-muted">{scanProgress.phase === 'verifying' ? 'Verifying camera connectivity...' : 'Scanning network...'}</span>
                        <span className="small fw-semibold">{scanProgress.phase === 'verifying' ? `${scanProgress.verifyPercent || 0}%` : `${scanProgress.percent}%`}</span>
                    </div>
                    <ProgressBar now={scanProgress.phase === 'verifying' ? (scanProgress.verifyPercent || 0) : scanProgress.percent}
                        variant={scanProgress.phase === 'verifying' ? 'success' : 'primary'} animated style={{ height: '6px' }} />
                </div>
            )}
            {scanMessage && !scanning && (
                <Alert variant={scanResults.length > 0 ? 'success' : 'info'} className="py-2 small mb-3">
                    <i className={`bi ${scanResults.length > 0 ? 'bi-check-circle' : 'bi-info-circle'} me-1`}></i>{scanMessage}
                </Alert>
            )}
            {scanResults.length > 0 && (
                <div className="list-group">
                    {scanResults.map((cam, idx) => (
                        <button key={idx} type="button" className="list-group-item list-group-item-action py-2 px-3" onClick={() => handleSelectCamera(cam)}>
                            <div className="d-flex align-items-center justify-content-between">
                                <span className="fw-medium small">
                                    {cam.connected ? <i className="bi bi-check-circle-fill me-2 text-success"></i> : <i className="bi bi-exclamation-circle me-2 text-warning"></i>}
                                    {cam.host}:{cam.port}{cam.brand && <span className="text-muted"> ({cam.brand})</span>}
                                </span>
                                <span className={`badge ${cam.connected ? 'bg-success' : 'bg-warning'} bg-opacity-10 ${cam.connected ? 'text-success' : 'text-warning'} small`}>
                                    {cam.connected ? 'Connected' : 'Port Open'}
                                </span>
                            </div>
                            {cam.streamPath && <div className="small mt-1 ms-4 font-monospace text-muted">{cam.streamPath}</div>}
                        </button>
                    ))}
                </div>
            )}
        </>
    )

    const renderConfigView = () => (
        <>
            <Form.Group className="mb-3">
                <Form.Label className="small fw-semibold text-muted mb-1">Protocol</Form.Label>
                <Form.Select size="sm" value={camera.protocol}
                    onChange={(e) => {
                        onChange('protocol', e.target.value)
                        if (!camera.port || ['554', '80', '443'].includes(camera.port)) onChange('port', getDefaultPort(e.target.value))
                    }}>
                    <option value="rtsp">RTSP</option><option value="http">HTTP</option><option value="https">HTTPS</option>
                </Form.Select>
            </Form.Group>
            <div className="row mb-3">
                <div className="col-8"><Form.Group>
                    <Form.Label className="small fw-semibold text-muted mb-1">Host / IP Address <span className="text-danger">*</span></Form.Label>
                    <Form.Control size="sm" type="text" value={camera.host} onChange={(e) => onChange('host', e.target.value)} placeholder="e.g., 192.168.1.100" />
                </Form.Group></div>
                <div className="col-4"><Form.Group>
                    <Form.Label className="small fw-semibold text-muted mb-1">Port</Form.Label>
                    <Form.Control size="sm" type="text" value={camera.port} onChange={(e) => onChange('port', e.target.value)} placeholder={getDefaultPort(camera.protocol)} />
                </Form.Group></div>
            </div>
            <div className="row mb-3">
                <div className="col-6"><Form.Group>
                    <Form.Label className="small fw-semibold text-muted mb-1">Username</Form.Label>
                    <Form.Control size="sm" type="text" value={camera.username} onChange={(e) => onChange('username', e.target.value)} placeholder="admin" />
                </Form.Group></div>
                <div className="col-6"><Form.Group>
                    <Form.Label className="small fw-semibold text-muted mb-1">Password</Form.Label>
                    <Form.Control size="sm" type="password" value={camera.password} onChange={(e) => onChange('password', e.target.value)} placeholder="********" />
                </Form.Group></div>
            </div>
            <Form.Group className="mb-3">
                <Form.Label className="small fw-semibold text-muted mb-1">Stream Path</Form.Label>
                <Form.Control size="sm" type="text" value={camera.path} onChange={(e) => onChange('path', e.target.value)} placeholder="/Streaming/Channels/401" />
                <Form.Text className="text-muted small">
                    {camera.protocol === 'rtsp' ? 'e.g., /Streaming/Channels/401 for Hikvision' : 'e.g., /ISAPI/Streaming/channels/401/picture'}
                </Form.Text>
            </Form.Group>
            {camera.host && (
                <div className="bg-light rounded p-2 mb-3">
                    <Form.Label className="small fw-semibold text-muted mb-1">Preview URL</Form.Label>
                    <div className="small text-break font-monospace">
                        {camera.protocol}://{camera.username && <>{camera.username}:***@</>}{camera.host}{camera.port && <>:{camera.port}</>}{camera.path}
                    </div>
                </div>
            )}
            {testImage && (
                <div className="mb-3 border rounded p-2">
                    <Form.Label className="small fw-semibold text-success mb-1"><i className="bi bi-check-circle me-1"></i>Connection Successful</Form.Label>
                    <img src={`data:${testImage.contentType};base64,${testImage.data}`} alt="Camera preview" className="w-100 rounded" style={{ maxHeight: '200px', objectFit: 'contain' }} />
                </div>
            )}
            {testError && <Alert variant="danger" className="mb-3 py-2 small"><i className="bi bi-x-circle me-1"></i>{testError}</Alert>}
            <div className="d-flex gap-2">
                <Button variant="info" size="sm" className="text-white" onClick={handleTest} disabled={testing || !camera.host}>
                    {testing ? <><Spinner size="sm" animation="border" className="me-1" />Testing</> : <><i className="bi bi-plug me-1"></i>Test Connection</>}
                </Button>
                <Button variant="secondary" size="sm" onClick={openScanView}><i className="bi bi-wifi me-1"></i>Scan Network</Button>
            </div>
        </>
    )

    return (
        <Card className="mb-3 border">
            <Card.Header className="bg-white py-2 px-3 border-bottom">
                <div className="d-flex align-items-center">
                    <i className={`bi ${scanView ? 'bi-wifi' : 'bi-camera-video'} me-2 text-primary`}></i>
                    <span className="fw-semibold small">{scanView ? 'Scan Network for Cameras' : 'Camera'}</span>
                </div>
            </Card.Header>
            <Card.Body className="p-3">
                {scanView ? renderScanView() : renderConfigView()}
            </Card.Body>
        </Card>
    )
}

export default CameraConfigSection
